import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import { Banner } from '@/components/banners/Banner'
import { Table } from '@/components/tables/Table/Table'
import {
  activateConfigGeneration,
  fetchConfigGenerations,
} from '@/reducers/voiceAgents/voiceAgents.actions'

// Org-1-only modal: every stored generation of this agent's config, and the
// switch between them.
//
// A generation is a structural version of the agent's design. Overrides belong
// to exactly one of them, so activating a generation brings that generation's
// Studio edits back with it and leaves the current one's dormant — nothing is
// copied and nothing is deleted, which is what makes the switch reversible in
// both directions.
const ConfigGenerationsModal = ({ show, onClose }) => {
  const dispatch = useDispatch()
  const agentId = useSelector((state) => state.voiceAgents.selectedAgentId)
  const activating = useSelector((state) => state.voiceAgents.loading?.activateGeneration)

  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  // The list thunk swallows failures and returns null, which is also what
  // "not fetched yet" looks like — without this the modal would sit on
  // "Loading…" forever after a backend hiccup, with no error and no retry.
  const [loadFailed, setLoadFailed] = useState(false)
  // Which row's button was pressed, so only that one shows a spinner.
  const [pending, setPending] = useState(null)

  const load = useCallback(async () => {
    const result = await dispatch(fetchConfigGenerations(agentId))
    return result
  }, [dispatch, agentId])

  useEffect(() => {
    if (!show) {
      setData(null)
      setError(null)
      setLoadFailed(false)
      setPending(null)
      return undefined
    }
    if (!agentId) {
      return undefined
    }
    // The fetch can outlive the modal — without the cancel guard a late resolve
    // would repopulate `data` after the close-reset above.
    let cancelled = false
    load().then((result) => {
      if (cancelled) {
        return
      }
      setLoadFailed(isNil(result))
      setData(result)
    })
    return () => {
      cancelled = true
    }
  }, [show, agentId, load])

  const onActivate = async (generation) => {
    setError(null)
    setPending(generation)
    const result = await dispatch(activateConfigGeneration(agentId, generation))
    if (result?.ok) {
      const refreshed = await load()
      setLoadFailed(isNil(refreshed))
      setData(refreshed)
    } else if (result?.error) {
      setError(result.error)
    }
    setPending(null)
  }

  const generations = data?.generations ?? []
  const activeGeneration = data?.activeGeneration

  return (
    <BasicModal
      title={agentId ? `Config generations — ${agentId}` : 'Config generations'}
      show={show}
      onClose={onClose}
      size="large"
      scrolling
      footer={
        <div className="voice-agents__import-modal-footer">
          <Button basic onClick={onClose} data-testid="generations-close">
            Close
          </Button>
        </div>
      }
    >
      <p className="voice-agents__reseed-hint">
        Each generation is a structural version of this agent. Studio edits belong to the generation
        they were made on, so <strong>activating one brings its edits back</strong> and leaves the
        current generation&apos;s untouched for a switch back. Nothing is deleted either way.
      </p>

      {loadFailed && (
        <Banner critical header="Could not load generations" data-testid="generations-load-error">
          <p>The list could not be read. Close and reopen to try again.</p>
        </Banner>
      )}

      {!loadFailed && isEmpty(generations) && !error && (
        <p data-testid="generations-empty">
          {isNil(data) ? 'Loading…' : 'This agent has a single generation.'}
        </p>
      )}

      {!isEmpty(generations) && (
        <Table borderless compact className="mt mb" data-testid="generations-table">
          <thead>
            <tr>
              <th>Generation</th>
              <th>Studio edits</th>
              <th>Config version</th>
              <th>Last updated</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {generations.map((row) => (
              <tr
                key={row.generation}
                className={row.isActive ? 'voice-agents__generations-row--active' : undefined}
                data-testid={`generations-row-${row.generation}`}
              >
                <td>
                  <strong>{row.generation}</strong>
                  {row.isActive && (
                    <span className="voice-agents__generations-active-pill">Active</span>
                  )}
                </td>
                <td data-testid={`generations-overrides-${row.generation}`}>{row.overrideCount}</td>
                <td>{row.configVersion}</td>
                <td>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}</td>
                <td>
                  {!row.isActive && (
                    <Button
                      onClick={() => onActivate(row.generation)}
                      disabled={activating}
                      loading={pending === row.generation}
                      data-testid={`generations-activate-${row.generation}`}
                    >
                      Make active
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {!isNil(activeGeneration) && (
        <Banner informative header="This changes what Studio shows, not what the agent runs">
          <p>
            A deployed agent carries its own generation in its <code>prompt_config.yaml</code>.
            Until it is rebuilt and redeployed to match generation{' '}
            <strong>{activeGeneration}</strong>, it keeps serving its own — and edits saved here
            will not reach it.
          </p>
        </Banner>
      )}

      {error && (
        <Banner critical header="Could not switch generation" data-testid="generations-error">
          <p>{error}</p>
        </Banner>
      )}
    </BasicModal>
  )
}

export default ConfigGenerationsModal
